import { type FC } from 'react';

import { toast } from 'react-toastify/unstyled';

import { useDeleteUserMutation } from '@features/user-management/api/user-management.api';
import type { User } from '@features/user-management/api/user-management.types';
import { GenericButton, GenericModal } from '@shared/components/ui/custom';
import type { RTKQueryError } from '@shared/utils/api.types';

export interface DeleteUserModalProps {
  onClose: () => void;
  rowData: User;
}

export const DeleteUserModal: FC<DeleteUserModalProps> = ({ onClose, rowData }) => {
  const [deleteUser, { isLoading, reset }] = useDeleteUserMutation();

  const handleClose = () => {
    onClose();
    reset?.();
  };

  const handleSubmit = async () => {
    try {
      await deleteUser(rowData.email).unwrap();
      toast.success(`User ${rowData.email} has been deleted.`);
      handleClose();
    } catch (err) {
      const error = err as RTKQueryError;
      const errorMessage = error?.data?.message ?? error?.message ?? 'Unknown error';
      toast.error(`Error performing the request: ${errorMessage}`);
    }
  };

  return (
    <GenericModal
      open
      onOpenChange={handleClose}
      title="Are you absolutely sure?"
      data-testid="delete-user-modal"
      description={
        <span>
          This action cannot be undone. This will permanently delete the user{' '}
          <span className="font-bold">{rowData.email}</span> from the system.
        </span>
      }
    >
      <div className="flex justify-end space-x-2" data-testid="delete-user-content">
        <GenericButton variant="outline" label="Cancel" onClick={handleClose} data-testid="cancel-delete-button" />
        <GenericButton
          variant="destructive"
          label="Delete"
          onClick={handleSubmit}
          isLoading={isLoading}
          data-testid="confirm-delete-button"
        />
      </div>
    </GenericModal>
  );
};
